<template>
  <div class="relative ml-6 flex flex-col items-start">
    <div v-if="!isLoadsDashboard">
      <button
        class="absolute left-0 top-0 flex min-w-max gap-1 rounded-md bg-gray-200 px-3 py-1 text-sm"
        @click="toggleFilters"
      >
        <img
          src="@/assets/filters-icon.svg"
          alt="filters"
        />
        {{ showFilters ? 'Hide' : 'Show' }} Filters
      </button>

      <div v-if="!isLoadsDashboard">
        <p class="absolute left-[136px] top-1 min-w-max">
          {{ numberOfLoads }}
        </p>
      </div>

      <div
        v-if="isLoadsDashboard && queryFilters?.length > 0"
        class="absolute left-[136px] top-2 flex gap-2"
      >
        <p
          class="mr-1 min-w-max text-sm font-normal leading-none text-neutral-subtlest"
        >
          Filtered by:
        </p>

        <div class="flex w-[calc(100vw-300px)] flex-wrap gap-1 xl:w-[1200px]">
          <div
            v-for="[type, filter] in queryFilters"
            :key="filter"
            class="flex min-w-max items-center gap-1 rounded-md bg-gray-200 px-2"
          >
            <p class="text-xs capitalize">
              {{ formattedFilterType(type) }}: {{ filter?.replace(/_/g, ' ') }}
            </p>
            <img
              class="h-1.5 w-1.5 cursor-pointer"
              src="@/assets/close-icon-gray.svg"
              alt="close icon"
              @click="handleRemoveFilter(type, filter)"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showFilters"
      class="mr-4 min-h-[calc(100vh-250px)] w-[350px] rounded-md border border-steel-300 bg-white shadow-md"
    >
      <div
        class="overflow-scroll"
        :class="{
          'max-h-[calc(100vh-230px)] min-h-[calc(100vh-230px)]': !bannerError,
          'max-h-[calc(100vh-259px)] min-h-[calc(100vh-259px)]': bannerError,
        }"
      >
        <span
          v-if="!isLoadsDashboard"
          class="flex w-full cursor-pointer items-center justify-center rounded-t-md bg-sun-400 px-2 text-sm"
          :class="{
            'text-info-bold-text bg-sun-400 hover:bg-yellow-200 hover:text-yellow-700':
              !resetActive,
            'bg-yellow-300 text-yellow-800': resetActive,
          }"
          @click="handleResetFilters"
        >
          Reset

          <svg
            class="ml-1 h-4 w-4"
            :class="{ 'spin-quick': resetActive }"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </span>

        <CollapsibleComponent class="mt-4">
          <template #title>
            <div
              class="flex items-center justify-between rounded-lg bg-white py-2 pl-3 pr-1 text-xs text-dark-gray"
            >
              <p class="font-semibold text-steel-1100">Location</p>
            </div>
          </template>
          <template #content>
            <div
              class="flex cursor-auto items-center justify-between pl-3 pt-2"
            >
              <div class="w-full">
                <div class="relative flex flex-col gap-2 pb-2">
                  <div class="flex items-center">
                    <p
                      class="w-full text-left text-xs font-semibold uppercase text-neutral-boldest"
                    >
                      Origin
                    </p>
                    <p
                      v-if="isLoadsDashboard"
                      class="w-[120px] text-left text-xs font-semibold uppercase text-neutral-boldest"
                    >
                      DH-O
                    </p>
                  </div>
                  <div class="flex gap-3">
                    <LocationSearchInput
                      type="origin"
                      placeholder="Select an Origin"
                      :is-slim="false"
                      :input-value="filters.origin || ''"
                      :show-clear-icon="true"
                      @set-location="setOriginFilters"
                      @set-selected-location="setSelectedOrigin"
                    ></LocationSearchInput>
                    <div
                      v-if="isLoadsDashboard"
                      class="dashboard-origin-dropdown relative w-full min-w-[90px] flex-1"
                    >
                      <dropdown-select
                        :options="deadheadOptions"
                        :default="selectedOriginDeadhead"
                        :value="selectedOriginDeadhead"
                        :exclude-selected="true"
                        class="!h-12 !rounded-md border border-light-gray !bg-lighter-gray !bg-opacity-30 text-xs"
                        @input="handleOriginDeadheadSelected"
                      />
                    </div>
                  </div>
                </div>

                <div class="relative flex flex-col gap-2 pb-2">
                  <div class="flex items-center">
                    <p
                      class="w-full text-left text-xs font-semibold uppercase text-neutral-boldest"
                    >
                      Destination
                    </p>
                    <p
                      v-if="isLoadsDashboard"
                      class="w-[120px] text-left text-xs font-semibold uppercase text-neutral-boldest"
                    >
                      DH-D
                    </p>
                  </div>
                  <div class="load-dashboard-location-input flex gap-3">
                    <LocationSearchInput
                      type="destination"
                      placeholder="Select a Destination"
                      :is-slim="false"
                      :show-clear-icon="true"
                      :input-value="filters.destination || ''"
                      @set-location="setDestinationFilters"
                      @set-selected-location="setSelectedDestination"
                    ></LocationSearchInput>
                    <div
                      v-if="isLoadsDashboard"
                      class="dashboard-destination-dropdown relative w-full min-w-[90px] flex-1"
                    >
                      <dropdown-select
                        :options="deadheadOptions"
                        :default="selectedDestinationDeadhead"
                        :value="selectedDestinationDeadhead"
                        :exclude-selected="true"
                        class="!h-12 !rounded-md border border-light-gray !bg-lighter-gray !bg-opacity-30 text-xs"
                        @input="handleDestinationDeadheadSelected"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex w-[50px] justify-center">
                <img
                  :class="{
                    'cursor-not-allowed opacity-30': !hasOriginOrDestination,
                    'cursor-pointer hover:opacity-60': hasOriginOrDestination,
                  }"
                  src="@/assets/arrows-up-and-down.svg"
                  alt="arrows"
                  @click="handleSwitchLocation"
                />
              </div>
            </div>
          </template>
        </CollapsibleComponent>

        <CollapsibleComponent>
          <template #title>
            <div
              class="flex items-center justify-between bg-white py-2 pl-3 pr-1 text-xs text-dark-gray"
            >
              <p class="font-semibold text-steel-1100">Broker</p>
            </div>
          </template>
          <template #content>
            <div class="relative p-3">
              <form
                @submit.prevent
                class="relative mb-2 flex items-center gap-2"
              >
                <input
                  v-model="filters.searchQuery"
                  type="text"
                  class="h-12 w-full rounded-md border-light-gray bg-lighter-gray bg-opacity-30 pr-16 text-sm text-gray-500 shadow-sm outline-none"
                  placeholder="Search Broker"
                  maxLength="100"
                  @input="handleInput"
                />
                <button
                  type="button"
                  class="absolute bottom-0 right-0 top-0 flex w-8 flex-col justify-center bg-transparent text-placeholder-gray hover:text-yellow-500"
                >
                  <img
                    class="h-4 w-4 cursor-pointer"
                    src="@/assets/search-light-gray.svg"
                    alt="search icon"
                  />
                </button>

                <img
                  v-if="filters.searchQuery"
                  class="absolute right-12 top-[18px] h-3 w-3 cursor-pointer opacity-30"
                  src="@/assets/close-icon.svg"
                  alt="close icon"
                  @click="clearSearchInput"
                />
              </form>
            </div>
          </template>
        </CollapsibleComponent>

        <CollapsibleComponent>
          <template #title>
            <div
              class="flex items-center justify-between bg-white py-2 pl-3 pr-1 text-xs text-dark-gray"
            >
              <p class="font-semibold text-steel-1100">Dates</p>
            </div>
          </template>
          <template #content>
            <div class="px-3 pb-3">
              <div class="relative mt-2 w-full">
                <p
                  class="text-left text-xs font-semibold uppercase text-neutral-boldest"
                >
                  Pickup Date Range
                </p>
                <form-field-date
                  label=" "
                  placeholder=""
                  :model-value="pickupPlaceholder"
                  name="pickup_date"
                  input-type="date"
                  :isSlim="true"
                  :hidden="false"
                  :monospace="false"
                  :show-confirm-button="false"
                  @update:model-value="dynamicUpdatePickupDate"
                  @toggle-date-picker="toggleShowPickupDatePicker"
                />

                <img
                  v-if="pickupPlaceholder"
                  class="absolute right-14 top-10 z-30 h-3 w-3 cursor-pointer opacity-30"
                  src="@/assets/close-icon.svg"
                  alt="close icon"
                  @click="clearPickup"
                />

                <div
                  v-show="showPickupDatePicker"
                  ref="startCalendarRef"
                  class="absolute right-0 top-full z-50 rounded-lg bg-white p-4 shadow-md"
                >
                  <date-picker-range
                    :start-date="startPickupDate"
                    :end-date="endPickupDate"
                    :field="'pickup_datepicker'"
                    date-format="MM/DD/YYYY"
                    :disable-previous="true"
                    :restrict-previous="true"
                    :restrict-year-date="true"
                    @day-selected="setSelectedPickupDay"
                  />
                </div>
              </div>

              <div class="relative mt-2 w-full">
                <p
                  class="text-left text-xs font-semibold uppercase text-neutral-boldest"
                >
                  Dropoff Date Range
                </p>
                <form-field-date
                  label=" "
                  placeholder=""
                  :model-value="dropoffPlaceholder"
                  name="pickup_date"
                  input-type="date"
                  :isSlim="true"
                  :hidden="false"
                  :monospace="false"
                  :show-confirm-button="false"
                  @update:model-value="dynamicUpdateDropoffDate"
                  @toggle-date-picker="toggleShowDropoffDatePicker"
                />

                <img
                  v-if="dropoffPlaceholder"
                  class="absolute right-14 top-10 z-30 h-3 w-3 cursor-pointer opacity-30"
                  src="@/assets/close-icon.svg"
                  alt="close icon"
                  @click="clearDropoff"
                />

                <div
                  v-show="showDropoffDatePicker"
                  ref="endCalendarRef"
                  class="absolute right-0 top-full z-50 rounded-lg bg-white p-4 shadow-md"
                >
                  <date-picker-range
                    :start-date="startDropoffDate"
                    :end-date="endDropoffDate"
                    :field="'pickup_datepicker'"
                    date-format="MM/DD/YYYY"
                    :disable-previous="true"
                    :restrict-previous="true"
                    :restrict-year-date="true"
                    @day-selected="setSelectedDropoffDay"
                  />
                </div>
              </div>
            </div>
          </template>
        </CollapsibleComponent>

        <CollapsibleComponent v-if="isLoadsDashboard">
          <template #title>
            <div
              class="flex items-center justify-between bg-white py-2 pl-3 pr-1 text-xs text-dark-gray"
            >
              <p class="font-semibold text-steel-1100">Equipment Types</p>
            </div>
          </template>
          <template #content>
            <div class="flex flex-wrap gap-2 p-3">
              <span
                v-for="[key, value] in Object.entries(equipment_types)"
                :key="key"
                class="w-max cursor-pointer rounded-full px-2 text-sm capitalize"
                :class="{
                  'bg-yellow-500 hover:bg-opacity-60':
                    filters.trailer?.includes(key),
                  'bg-lighter-gray hover:bg-placeholder-gray':
                    !filters.trailer?.includes(key),
                }"
                @click="handleSelectItem(key, 'trailer')"
              >
                {{ value }}
              </span>
            </div>
          </template>
        </CollapsibleComponent>

        <CollapsibleComponent v-if="isLoadsDashboard">
          <template #title>
            <div
              class="flex items-center justify-between bg-white py-2 pl-3 pr-1 text-xs text-dark-gray"
            >
              <p class="font-semibold text-steel-1100">Rates</p>
            </div>
          </template>
          <template #content>
            <div class="relative flex flex-col gap-2 px-3 pb-3">
              <p
                class="flex items-center justify-between rounded-lg bg-white py-1 pr-1 text-dark-gray"
              >
                Minimum Rate Flat
              </p>

              <input
                v-model="filters.min_rate"
                class="h-9 w-full rounded-md border-light-gray bg-lighter-gray bg-opacity-30 px-3 text-sm text-gray-500 shadow-sm outline-none"
                placeholder="Set Minimum Rate"
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                @input="handleInput"
              />

              <img
                v-if="filters.min_rate"
                class="absolute right-8 top-[50px] h-3 w-3 cursor-pointer opacity-30"
                src="@/assets/close-icon.svg"
                alt="close icon"
                @click="clearMinRate"
              />
            </div>
          </template>
        </CollapsibleComponent>

        <div v-if="isLoadsDashboard">
          <div class="flex justify-between px-3 py-2">
            <p
              class="flex items-center justify-between rounded-lg bg-white text-sm font-semibold text-steel-1100"
            >
              Connected Brokers Only
            </p>

            <toggle-icon
              :model-value="!!filters.status"
              bg-class="bg-steel-100"
              @update:model-value="handleStatusFilter"
            ></toggle-icon>
          </div>
        </div>

        <CollapsibleComponent v-if="isLoadsDashboard">
          <template #title>
            <div
              class="flex items-center justify-between bg-white py-2 pl-3 pr-1 text-xs text-dark-gray"
            >
              <p class="font-semibold text-steel-1100">Posting Age</p>
            </div>
          </template>
          <template #content>
            <div class="flex flex-wrap gap-2 p-3">
              <span
                v-for="posting in posting_types"
                :key="posting.value"
                class="w-max cursor-pointer rounded-full px-2 text-sm capitalize"
                :class="{
                  'bg-yellow-500 hover:bg-opacity-60':
                    filters.posting?.includes(posting.value),
                  'bg-lighter-gray hover:bg-placeholder-gray':
                    !filters.posting?.includes(posting.value),
                }"
                @click="handleSelectSingleItem(posting.value, 'posting')"
              >
                {{ posting.title }}
              </span>
            </div>
          </template>
        </CollapsibleComponent>

        <CollapsibleComponent v-if="isLoadsDashboard">
          <template #title>
            <div
              class="flex items-center justify-between bg-white py-2 pl-3 pr-1 text-xs text-dark-gray"
            >
              <p class="font-semibold text-steel-1100">Type</p>
            </div>
          </template>
          <template #content>
            <div class="flex flex-wrap gap-2 p-3">
              <span
                v-for="type in types"
                :key="type.value"
                class="w-max cursor-pointer rounded-full px-2 text-sm capitalize"
                :class="{
                  'bg-yellow-500 hover:bg-opacity-60': filters[type.value],
                  'bg-lighter-gray hover:bg-placeholder-gray':
                    !filters[type.value],
                }"
                @click="handleSelectType(type.value)"
              >
                {{ type.title }}
              </span>
            </div>
          </template>
        </CollapsibleComponent>

        <CollapsibleComponent v-if="isLoadsDashboard">
          <template #title>
            <div
              class="flex items-center justify-between bg-white py-2 pl-3 pr-1 text-xs font-semibold text-steel-1100"
            >
              <p class="font-semibold text-steel-1100">Weight</p>
            </div>
          </template>
          <template #content>
            <div class="relative p-3">
              <input
                v-model="filters.weight"
                class="h-9 w-full rounded-md border-light-gray bg-lighter-gray bg-opacity-30 px-3 text-sm text-gray-500 shadow-sm outline-none"
                placeholder="Set Max Weight"
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                @input="handleInput"
              />

              <img
                v-if="filters.weight"
                class="absolute right-8 top-[24px] h-3 w-3 cursor-pointer opacity-30"
                src="@/assets/close-icon.svg"
                alt="close icon"
                @click="clearWeight"
              />
            </div>
          </template>
        </CollapsibleComponent>

        <CollapsibleComponent v-if="!isLoadsDashboard">
          <template #title>
            <div
              class="flex items-center justify-between rounded-lg bg-white py-2 pl-3 pr-1 text-xs text-dark-gray"
            >
              <p>Equipment Types</p>
            </div>
          </template>
          <template #content>
            <div class="flex flex-wrap gap-2 p-3">
              <span
                v-for="[key, value] in Object.entries(equipment_types)"
                :key="key"
                class="w-max cursor-pointer rounded-full bg-lighter-gray px-2 text-sm capitalize"
                :class="{
                  'bg-yellow-500': filters.equipment?.includes(key),
                }"
                @click="handleSelectItem(key)"
              >
                {{ value }}
              </span>
            </div>
          </template>
        </CollapsibleComponent>
      </div>

      <div
        v-if="isLoadsDashboard"
        class="flex w-full justify-end rounded-b-lg border-t border-steel-300 bg-white px-2 py-3 pr-4"
      >
        <ButtonComponent @click="handleResetFilters">Reset</ButtonComponent>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import dayjs from 'dayjs';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import debounce from 'lodash.debounce';
import {
  ButtonComponent,
  DatePickerRange,
  FormFieldDate,
  SingleToggleIcon as ToggleIcon,
  CollapsibleComponent,
} from '@/components';
import {
  LocationSearchInput,
  LoadDropdown as DropdownSelect,
} from '@/components/loads';
import { createFilterQuery, filterKeys, useClickAway } from '@/utils';

const props = defineProps({
  isLoadsDashboard: Boolean,
  isResettingSearch: Boolean,
  isRefreshSearch: Boolean,
});

const store = useStore();
const router = useRouter();

const deadheadOptions = [
  '50 mi',
  '100 mi',
  '150 mi',
  '200 mi',
  '250 mi',
  '300 mi',
  '350 mi',
  '400 mi',
  '450 mi',
  '500 mi',
];

const types = [
  {
    title: 'Hazmat',
    value: 'hazmat',
  },
  {
    title: 'Teams',
    value: 'teams',
  },
];

const posting_types = [
  {
    title: 'All Postings',
    value: 'all',
  },
  {
    title: '< 8 Hours',
    value: '8hr',
  },
  {
    title: '8-12 Hours',
    value: '12hr',
  },
  {
    title: '12-24 Hours',
    value: '24hr',
  },
  {
    title: '24-72 Hours',
    value: '72hr',
  },
  {
    title: '72+ Hours',
    value: '>72hr',
  },
];

const showFilters = ref(true);

const toggleFilters = () => {
  showFilters.value = !showFilters.value;
};

const equipmentQuery = Array.isArray(router.currentRoute.value.query?.equipment)
  ? router.currentRoute.value.query?.equipment
  : [router.currentRoute.value.query?.equipment];

const trailerQuery = Array.isArray(router.currentRoute.value.query?.trailer)
  ? router.currentRoute.value.query?.trailer
  : [router.currentRoute.value.query?.trailer];

const loadQuery = Array.isArray(router.currentRoute.value.query?.load)
  ? router.currentRoute.value.query?.load
  : [router.currentRoute.value.query?.load];

const defaultFilters = {
  origin: '',
  destination: '',
  selectedOrigin: '',
  selectedDestination: '',
  equipment: router.currentRoute.value.query?.equipment ? equipmentQuery : [],
  trailer: router.currentRoute.value.query?.trailer ? trailerQuery : [],
  load: router.currentRoute.value.query?.load ? loadQuery : [],
  pickup_start: router.currentRoute.value.query?.pickup_start || '',
  pickup_end: router.currentRoute.value.query?.pickup_end || '',
  dropoff_start: router.currentRoute.value.query?.dropoff_start || '',
  dropoff_end: router.currentRoute.value.query?.dropoff_end || '',
  searchQuery: router.currentRoute.value.query?.query || '',
  certifications: router.currentRoute.value.query?.certifications || [],
  status: router.currentRoute.value.query?.status || false,
  posting: router.currentRoute.value.query?.posting || [],
  rate: router.currentRoute.value.query?.rate || [],
  team: router.currentRoute.value.query?.team || false,
  hazmat: router.currentRoute.value.query?.hazmat || false,
  power_only: router.currentRoute.value.query?.power_only || false,
  min_rate: router.currentRoute.value.query?.min_rate || '',
  weight: router.currentRoute.value.query?.weight || '',
  origin_deadhead: router.currentRoute.value.query?.origin_deadhead || '150 mi',
  destination_deadhead:
    router.currentRoute.value.query?.destination_deadhead || '150 mi',
};

const filters = ref(defaultFilters);

const pickupPlaceholder = computed(() => {
  if (filters.value.pickup_start && filters.value.pickup_end) {
    return `${dayjs(filters.value.pickup_start).format('MMM DD, YYYY')} - ${dayjs(filters.value.pickup_end).format('MMM DD, YYYY')}`;
  } else {
    return '';
  }
});
const dropoffPlaceholder = computed(() => {
  if (filters.value.dropoff_start && filters.value.dropoff_end) {
    return `${dayjs(filters.value.dropoff_start).format('MMM DD, YYYY')} - ${dayjs(filters.value.dropoff_end).format('MMM DD, YYYY')}`;
  } else {
    return '';
  }
});

const bannerError = computed(
  () =>
    !store.state.errors.forceHideErrorBanner &&
    store.state.errors.showErrorBanner
);

const postingHash = {
  all: 'All',
  '8hr': '<8 hours',
  '12hr': '8-12 hours',
  '24hr': '12-24 hours',
  '72hr': '24-72 hours',
  '>72hr': '72+ hours',
};

const filteredByQueryItems = computed(() => ({
  origin: filters.value?.origin,
  destination: filters.value?.destination,
  equipment: filters.value?.equipment,
  trailer: filters.value?.trailer || [],
  pickup_start: filters.value?.pickup_start,
  pickup_end: filters.value?.pickup_end,
  dropoff_start: filters.value?.dropoff_start,
  dropoff_end: filters.value?.dropoff_end,
  status: filters.value?.status,
  certifications: filters.value?.certifications,
  posting: filters.value?.posting,
  team: filters.value?.team,
  hazmat: filters.value?.hazmat,
  power_only: filters.value?.power_only,
  min_rate: filters.value?.min_rate,
  weight: filters.value?.weight,
}));

const queryFilters = computed(() => {
  return Object.entries(filteredByQueryItems.value).reduce(
    (acc, [key, value]) => {
      if (Array.isArray(value)) {
        if (key === 'posting' && value?.length > 0) {
          acc.push([key, postingHash?.[value[0]]]);
        } else if (value?.length > 0) {
          const values = value.map((el) => [key, el]);
          acc.push(...values);
        }
      } else if (key === 'pickup_start' && value) {
        acc.push(['pickup', pickupPlaceholder.value]);
      } else if (key === 'dropoff_start' && value) {
        acc.push(['dropoff', dropoffPlaceholder.value]);
      } else if (key === 'pickup_end' || key === 'dropoff_end') {
        // noop
      } else if (key === 'team' && value) {
        acc.push([key, value.toString()]);
      } else if (key === 'hazmat' && value) {
        acc.push([key, value.toString()]);
      } else if (key === 'power_only' && value) {
        acc.push([key, value.toString()]);
      } else if (key === 'status' && value) {
        acc.push([key, 'In Network']);
      } else if (value) {
        acc.push([key, value]);
      }

      return acc;
    },
    []
  );
});

const formattedFilterType = (value) => {
  return filterKeys[value] || value;
};

const existingFilters = computed(() => store.state.loads.loadSearchFilters);

const equipment_types = computed(() => store.state.loads.equipment_types);

onMounted(async () => {
  await store.dispatch('loads/getEquipmentTypes');

  const pickupParams = router.currentRoute.value.query?.pickup;
  const dropoffParams = router.currentRoute.value.query?.dropoff;

  const sortedFilters = {
    origin: existingFilters.value?.origin,
    destination: existingFilters.value?.destination,
    selectedOrigin: existingFilters.value?.selectedOrigin,
    selectedDestination: existingFilters.value?.selectedDestination,
    equipment: existingFilters.value?.equipment,
    trailer: existingFilters.value?.trailer || [],
    pickup_start: existingFilters.value?.pickup_start,
    pickup_end: existingFilters.value?.pickup_end,
    dropoff_start: existingFilters.value?.dropoff_start,
    dropoff_end: existingFilters.value?.dropoff_end,
    certifications: existingFilters.value?.certifications || [],
    team: existingFilters.value?.team || false,
    hazmat: existingFilters.value?.hazmat || false,
    power_only: existingFilters.value?.power_only || false,
    posting: existingFilters.value?.posting || [],
    rate: existingFilters.value?.rate || [],
    status: existingFilters.value?.status || false,
    origin_area_id: existingFilters.value?.origin_area_id || '',
    destination_area_id: existingFilters.value?.destination_area_id || '',
    min_rate: existingFilters.value?.min_rate || '',
    weight: existingFilters.value?.weight || '',
    searchQuery: existingFilters.value?.searchQuery || '',
    origin_deadhead: existingFilters.value?.origin_deadhead || '150 mi',
    destination_deadhead:
      existingFilters.value?.destination_deadhead || '150 mi',
  };

  filters.value = sortedFilters;

  if (pickupParams) {
    selectedPickupDate.value = dayjs(pickupParams).format('MM/DD/YYYY');
  }
  if (dropoffParams) {
    selectedDropoffDate.value = dayjs(dropoffParams).format('MM/DD/YYYY');
  }

  selectedOriginDeadhead.value =
    existingFilters.value?.origin_deadhead?.toString() || '150 mi';
  selectedDestinationDeadhead.value =
    existingFilters.value?.destination_deadhead?.toString() || '150 mi';

  store.commit('loads/setDispatchFilters', filters.value);
});

const applyFilter = async (clearAll) => {
  store.commit('loads/setLoading', true);

  const filterParams = createFilterQuery(
    filters.value,
    router.currentRoute.value.query
  );

  router.push({
    query: clearAll ? {} : filterParams,
  });

  await store.commit('loads/setLoadSearchFilters', filters.value);

  const view = router.currentRoute.value?.query?.view;

  if (view === 'dispatch') {
    store.commit('loads/setDispatchFilters', filters.value);
  } else if (view === 'transit') {
    store.commit('loads/setTransitFilters', filters.value);
  } else if (view === 'completed') {
    store.commit('loads/setCompletedFilters', filters.value);
  }

  setTimeout(() => {
    // fallback
    store.commit('loads/setLoading', false);
  }, 10000);
};

const hasOriginOrDestination = computed(() => {
  return filters.value.origin || filters.value.destination;
});

const handleSwitchLocation = () => {
  if (!hasOriginOrDestination.value) {
    return;
  }

  const previousOrigin = filters.value.origin;
  const previousDestination = filters.value.destination;
  const previousOriginDeadhead = filters.value.origin_deadhead;
  const previousDestinationDeadhead = filters.value.destination_deadhead;
  const previousSelectedOrigin = filters.value.selectedOrigin;
  const previousSelectedDestination = filters.value.selectedDestination;
  const previousOriginAreaId = filters.value.origin_area_id;
  const previousDestinationAreaId = filters.value.destination_area_id;

  const updatedFilters = {
    ...filters.value,
    origin: previousDestination,
    destination: previousOrigin,
    origin_deadhead: previousDestinationDeadhead,
    destination_deadhead: previousOriginDeadhead,
    selectedOrigin: previousSelectedDestination,
    selectedDestination: previousSelectedOrigin,
    origin_area_id: previousDestinationAreaId,
    destination_area_id: previousOriginAreaId,
  };

  filters.value = updatedFilters;
  selectedOriginDeadhead.value = previousDestinationDeadhead;
  selectedDestinationDeadhead.value = previousOriginDeadhead;

  applyFilter();
};

watch(
  () => router.currentRoute.value?.query?.view,
  (newValue) => {
    if (newValue === 'dispatch') {
      filters.value = store.state.loads?.dispatch_filters;
    } else if (newValue === 'transit') {
      filters.value = store.state.loads?.transit_filters;
    } else {
      filters.value = store.state.loads?.completed_filters;
    }
  }
);

const company = computed(() => store.state.brokers.company);

watch(
  () => company.value?.legal_name,
  (newValue) => {
    if (newValue && router.currentRoute.value?.query?.company_id) {
      setTimeout(() => {
        filters.value.searchQuery = newValue;
      }, 200);
    }
  }
);

watch(
  () => filters.value?.pickup_end,
  (newValue) => {
    if (newValue && filters.value.dropoff_end) {
      const pickupEnd = dayjs(newValue);
      const dropoffStart = dayjs(filters.value.dropoff_start);
      const isDropoffBeforePickup = dropoffStart.isBefore(pickupEnd);

      if (isDropoffBeforePickup) {
        console.log(
          'Pickup date has been changed to be before dropoff date. Clearing dropoff'
        );
        clearDropoff();
      }
    }
  }
);

const selectedOriginDeadhead = ref('');
const selectedDestinationDeadhead = ref('');
const handleOriginDeadheadSelected = (origin) => {
  filters.value.origin_deadhead = origin;
  selectedOriginDeadhead.value = origin;

  applyFilter();
};
const handleDestinationDeadheadSelected = (destination) => {
  filters.value.destination_deadhead = destination;
  selectedDestinationDeadhead.value = destination;

  applyFilter();
};

const clearSearchInput = async () => {
  filters.value.searchQuery = '';

  if (
    router.currentRoute?.value?.query?.query ||
    router.currentRoute?.value?.query?.company_id
  ) {
    applyFilter();
  }
};

const showPickupDatePicker = ref(false);
const showDropoffDatePicker = ref(false);

const selectedPickupDate = ref(filters.value.pickup_start || '');
const selectedPickupEndDate = ref(filters.value.pickup_end || '');
const selectedDropoffDate = ref(filters.value.dropoff_start || '');
const selectedDropoffEndDate = ref(filters.value.dropoff_end || '');

const startPickupDate = computed(() => {
  return selectedPickupDate.value
    ? dayjs(selectedPickupDate.value).format('YYYY-MM-DD')
    : undefined;
});
const endPickupDate = computed(() => {
  return selectedPickupEndDate.value
    ? dayjs(selectedPickupEndDate.value).format('YYYY-MM-DD')
    : undefined;
});

const startDropoffDate = computed(() => {
  return selectedDropoffDate.value
    ? dayjs(selectedDropoffDate.value).format('YYYY-MM-DD')
    : undefined;
});
const endDropoffDate = computed(() => {
  return selectedDropoffEndDate.value
    ? dayjs(selectedDropoffEndDate.value).format('YYYY-MM-DD')
    : undefined;
});

function dynamicUpdatePickupDate({ value }) {
  if (value) {
    filters.value.pickup_start = selectedPickupDate.value;
    filters.value.pickup_end = selectedPickupEndDate.value;
  }

  applyFilter();
}

const setSelectedPickupDay = (fieldValue, dateSelected) => {
  if (
    !selectedPickupDate.value ||
    (selectedPickupDate.value && selectedPickupEndDate.value)
  ) {
    // set pickup start range
    selectedPickupDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    selectedPickupEndDate.value = undefined;
  } else {
    // set pickup end range
    selectedPickupEndDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    dynamicUpdatePickupDate({ name: fieldValue, value: dateSelected });
    showPickupDatePicker.value = false;
  }
};

const toggleShowPickupDatePicker = () => {
  showPickupDatePicker.value = !showPickupDatePicker.value;
};

function dynamicUpdateDropoffDate({ value }) {
  if (value) {
    filters.value.dropoff_start = selectedDropoffDate.value;
    filters.value.dropoff_end = selectedDropoffEndDate.value;
  }

  applyFilter();
}

const setSelectedDropoffDay = (fieldValue, dateSelected) => {
  if (
    !selectedDropoffDate.value ||
    (selectedDropoffDate.value && selectedDropoffEndDate.value)
  ) {
    // set dropoff start range
    selectedDropoffDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    selectedDropoffEndDate.value = undefined;
  } else {
    // set dropoff end range
    selectedDropoffEndDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    dynamicUpdateDropoffDate({ name: fieldValue, value: dateSelected });
    showDropoffDatePicker.value = false;
  }
};

const toggleShowDropoffDatePicker = () => {
  showDropoffDatePicker.value = !showDropoffDatePicker.value;
};

const setOriginFilters = (origin) => {
  filters.value.origin = origin;
};

const setSelectedOrigin = (origin) => {
  filters.value.origin_area_id = origin?.id;
  filters.value.selectedOrigin = origin?.code;

  applyFilter();
};

const setDestinationFilters = (destination) => {
  filters.value.destination = destination;
};

const setSelectedDestination = (destination) => {
  filters.value.destination_area_id = destination?.id;
  filters.value.selectedDestination = destination?.code;

  applyFilter();
};

const clearPickup = () => {
  filters.value.pickup_start = '';
  filters.value.pickup_end = '';
  selectedPickupDate.value = '';
  selectedPickupEndDate.value = '';

  applyFilter();
};

const clearDropoff = () => {
  filters.value.dropoff_start = '';
  filters.value.dropoff_end = '';
  selectedDropoffDate.value = '';
  selectedDropoffEndDate.value = '';

  applyFilter();
};

const clearMinRate = () => {
  filters.value.min_rate = '';

  applyFilter();
};

const clearWeight = () => {
  filters.value.weight = '';

  applyFilter();
};

const handleSelectItem = (item, type = 'equipment') => {
  if (filters.value?.[type]?.includes(item)) {
    const updatedFilters = filters.value[type].filter((i) => i !== item);
    filters.value[type] = updatedFilters;
  } else {
    filters.value[type].push(item);
  }

  applyFilter();
};

const handleSelectSingleItem = (item, type = 'posting') => {
  if (filters.value?.[type]?.includes(item)) {
    filters.value[type] = [];
  } else {
    filters.value[type] = [item];
  }

  applyFilter();
};

const handleSelectType = (type) => {
  if (filters.value?.[type] == true) {
    filters.value[type] = false;
  } else {
    filters.value[type] = true;
  }

  applyFilter();
};

const handleInput = debounce(() => {
  applyFilter();
}, 500);

const resetActive = ref(false);

const isFiltersEmpty = computed(() => {
  return !Object.values(filters.value).some((el) => {
    if (Array.isArray(el)) {
      return el?.length > 0;
    } else {
      return el;
    }
  });
});

watch(
  () => props.isResettingSearch,
  (newValue) => {
    if (newValue) {
      handleResetFilters();
    }
  }
);

watch(
  () => props.isRefreshSearch,
  (newValue) => {
    if (newValue) {
      filters.value = {
        trailer: store.state.loads?.loadSearchFilters?.trailer,
        origin: store.state.loads?.loadSearchFilters?.origin,
        destination: store.state.loads?.loadSearchFilters?.destination,
        pickup_start: store.state.loads?.loadSearchFilters?.pickup_start,
        pickup_end: store.state.loads?.loadSearchFilters?.pickup_end,
        origin_deadhead: store.state.loads?.loadSearchFilters?.origin_deadhead,
        destination_deadhead:
          store.state.loads?.loadSearchFilters?.destination_deadhead,
        origin_area_id: store.state.loads?.loadSearchFilters?.selectedOrigin,
        destination_area_id:
          store.state.loads?.loadSearchFilters?.selectedDestination,
      };

      applyFilter();
    }
  }
);

async function handleResetFilters() {
  if (!isFiltersEmpty.value) {
    console.log('clear filters');
    resetActive.value = true;
    filters.value = {
      equipment: [],
      trailer: [],
      load: [],
      origin: '',
      destination: '',
      searchQuery: '',
      pickup_start: '',
      pickup_end: '',
      dropoff_start: '',
      dropoff_end: '',
      deadhead: '',
      min_rate: '',
      weight: '',
      origin_deadhead: '150 mi',
      destination_deadhead: '150 mi',
      origin_area_id: '',
      destination_area_id: '',
      certifications: [],
      status: false,
      posting: [],
      rate: [],
      team: false,
      hazmat: false,
      power_only: false,
    };

    selectedPickupDate.value = '';
    selectedPickupEndDate.value = '';
    selectedDropoffDate.value = '';
    selectedDropoffEndDate.value = '';
    selectedDestinationDeadhead.value = '150 mi';
    selectedOriginDeadhead.value = '150 mi';

    await store.commit('laneSearches/setAutocompleteResults', {
      results: [],
      isType: 'origin',
    });
    await store.commit('laneSearches/setAutocompleteResults', {
      results: [],
      isType: 'destination',
    });

    applyFilter(true);

    setTimeout(() => {
      resetActive.value = false;
    }, 300);
  }
}

const handleRemoveFilter = (type, filter) => {
  console.log('Remove filter: ', type);
  if (type === 'pickup') {
    filters.value.pickup_start = '';
    filters.value.pickup_end = '';
  } else if (type === 'dropoff') {
    filters.value.dropoff_start = '';
    filters.value.dropoff_end = '';
  } else if (type === 'posting') {
    filters.value.posting = [];
  } else if (Array.isArray(filters.value?.[type])) {
    const values = filters.value[type]?.filter((el) => el !== filter);

    filters.value[type] = values;
  } else if (filters.value?.[type]) {
    filters.value[type] = '';

    if (type === 'origin') {
      filters.value.origin_area_id = '';
      filters.value.selectedOrigin = '';
    } else if (type === 'destination') {
      filters.value.destination_area_id = '';
      filters.value.selectedDestination = '';
    }
  }

  applyFilter();
};

const currentTab = computed(() => store.state.loads?.current_load_status);

const numberOfLoads = computed(() => {
  const totalLoads =
    store.state.loads?.[`${currentTab?.value}_pagination`]?.totalRecords;

  let loadText = '';
  if (totalLoads == 1) {
    loadText = '1 load';
  } else if (totalLoads > 1) {
    loadText = `${totalLoads} loads`;
  }

  return loadText;
});

const handleStatusFilter = (val) => {
  filters.value.status = val;

  applyFilter();
};

const startCalendarRef = ref();
useClickAway(startCalendarRef, (e) => {
  if (
    !e.target.classList?.contains('datepicker-btn') &&
    !e.target.classList?.contains('datepicker-calendar-icon')
  ) {
    showPickupDatePicker.value = false;
  }
});

const endCalendarRef = ref();
useClickAway(endCalendarRef, (e) => {
  if (
    !e.target.classList?.contains('datepicker-btn') &&
    !e.target.classList?.contains('datepicker-calendar-icon')
  ) {
    showDropoffDatePicker.value = false;
  }
});
</script>

<style>
.load-dashboard-location-input input {
  @apply text-[13px];
}
</style>
